import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { Router } from '@angular/router';

interface HeroScene {
  src: string;
  alt: string;
  /** CSS background-position; shift focal subject away from left copy when needed */
  position?: string;
  /** Optional CSS background-size (default cover) to allow stronger horizontal pan */
  size?: string;
  /** Extra rightward pan of the slide content (clears left headline) */
  shiftRight?: boolean;
}

@Component({
  selector: 'app-landing-home',
  templateUrl: './landing-home.component.html',
  styleUrls: ['../landing/landing.component.scss', '../landing/headers.css', './landing-home.component.scss']
})
export class LandingHomeComponent implements OnInit, OnDestroy {
  lightboxOpen = false;
  lightboxSrc = '';
  lightboxAlt = '';
  rtkExpertSiteUrl = '';

  readonly heroScenes: HeroScene[] = [
    {
      src: 'assets/images/landing/hero-scene-map.jpg',
      alt: 'High-precision site map with centimeter accuracy'
    },
    {
      src: 'assets/images/landing/hero-scene-robot.jpg',
      alt: 'Humanoid robot and drone on a construction platform'
    },
    {
      src: 'assets/images/landing/hero-scene-transit.jpg',
      alt: 'Urban transit corridor with high-precision route guidance'
    },
    {
      src: 'assets/images/landing/hero-scene-nature.jpg',
      alt: 'Coastal mountains and ocean with geospatial precision markers',
      // Crop left / enlarge + shift so pin+trail sit well right of headline
      position: '0% 42%',
      size: '155% auto',
      shiftRight: true
    }
  ];

  heroIndex = 0;
  private heroTimer: ReturnType<typeof setInterval> | null = null;
  private readonly heroIntervalMs = 6000;

  constructor(private auth: AuthService, private userService: UserService, private router: Router) {}

  ngOnInit(): void {
    this.userService.getPublicSiteUrls().subscribe({
      next: (cfg) => {
        this.rtkExpertSiteUrl = (cfg?.rtkExpertSiteURL || '').trim();
      },
      error: () => {
        this.rtkExpertSiteUrl = '';
      }
    });
    this.startHeroRotation();
  }

  ngOnDestroy(): void {
    this.stopHeroRotation();
  }

  goToHeroSlide(index: number): void {
    if (index < 0 || index >= this.heroScenes.length) {
      return;
    }
    this.heroIndex = index;
    this.startHeroRotation();
  }

  private startHeroRotation(): void {
    this.stopHeroRotation();
    if (this.heroScenes.length < 2) {
      return;
    }
    this.heroTimer = setInterval(() => {
      this.heroIndex = (this.heroIndex + 1) % this.heroScenes.length;
    }, this.heroIntervalMs);
  }

  private stopHeroRotation(): void {
    if (this.heroTimer != null) {
      clearInterval(this.heroTimer);
      this.heroTimer = null;
    }
  }

  login(): void { this.auth.login(); }
  signup(): void { this.auth.signup(); }

  scrollToRtkExpert(): void {
    document.getElementById('rtk-expert-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  goToSoftwarePage(): void {
    this.router.navigate(['/geoastra-software']).then(() => {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    });
  }

  goToReceiversPage(): void {
    this.router.navigate(['/geoastra-gnss-receivers']).then(() => {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    });
  }

  goToNtripCasterLearnMore(): void {
    this.router.navigate(['/landing-ntrip-caster']).then(() => {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    });
  }

  goToRtkExpertLearnMore(): void {
    const base = (this.rtkExpertSiteUrl || 'https://rtk-expert.geoastra.com').replace(/\/+$/, '');
    window.location.href = `${base}/`;
  }

  private buildRtkExpertUrl(action: 'login' | 'signup'): string {
    const base = (this.rtkExpertSiteUrl || 'https://rtk-expert.geoastra.com').replace(/\/+$/, '');
    const param = action === 'login' ? 'autoLogin=1' : 'autoSignup=1';
    return `${base}/?${param}`;
  }

  goToRtkExpertLogin(): void {
    window.location.href = this.buildRtkExpertUrl('login');
  }

  goToRtkExpertSignup(): void {
    window.location.href = this.buildRtkExpertUrl('signup');
  }

  openLightbox(src: string, alt: string): void {
    this.lightboxSrc = src;
    this.lightboxAlt = alt;
    this.lightboxOpen = true;
  }

  closeLightbox(): void {
    this.lightboxOpen = false;
    this.lightboxSrc = '';
    this.lightboxAlt = '';
  }
}
